import { useNavigation } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import fetch from 'cross-fetch';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { InferType } from 'yup';

import { useUpdateMeMutation } from '@graphql/generated';
import { useGoogleAuth } from '@hooks/useGoogleAuth';
import { useNotification } from '@hooks/useNotification.web';
import {
  SocialSignupFormStatus,
  useIsSocialSignup,
} from '@hooks/useSocialSignupReauth';
import i18n from '@i18n/i18n.config';
import RegisterStackNew from '@navigation/auth/register-stack-new.web';
import registerSchema from '@schemas/registerSchema.web';
import registerSocialSchema from '@schemas/registerSocialSchema.web';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useAuthContext } from '@src/context/authContext';
import { useMixpanel } from '@src/utils/mixpanel';
import { logEvent } from '@utils/eventLogger';
import { guessTimeZone } from '@utils/formatters/date';
import { getApiConfig } from '@utils/getApiConfig';
import { getApolloErrorMessage } from '@utils/getApolloErrorMessage';
import { parseRegisterResponse } from '@utils/parseRegisterResponse';
import { RATE_LIMIT_ERROR_KEY } from '@utils/rateLimit';
import { isSocialIdentityError } from '@utils/socialRegistration';
import { SecurityStorage } from '@utils/storage';
import {
  clearPendingTeamSignup,
  savePendingTeamSignup,
} from '@utils/teams/pendingTeamSignup';
import { getUtmEventProperties } from '@utils/utmParams';

import {
  getRegisterFieldErrors,
  getRegisterHistoryOffset,
  getRegisterMutationFieldErrors,
} from './registerFormFieldErrors';
export type RegisterFormValues = InferType<typeof registerSchema>;

const apiConfig = getApiConfig();

/**
 * [Formik field, its `*_server_error` companion, the mapper's logical key].
 * registerSchema.web turns each server_error value into `errors.<field>` via a
 * `no-server-error` test; registerSocialSchema.web does not, which is why the
 * Formik error is written alongside it.
 */
const SERVER_ERROR_FIELDS = [
  ['email', 'email_server_error', 'email'],
  ['password', 'password_server_error', 'password'],
  ['phone', 'phone_server_error', 'phone'],
  ['verify_code', 'verify_code_server_error', 'verificationCode'],
] as const;
const RegisterForm = () => {
  const { setAuthToken, setRegisterLoading } = useAuthContext();
  const { userInfo } = useGoogleAuth();
  const isSocialSignup = useIsSocialSignup();
  const { trackEvent } = useMixpanel();
  const navigation = useNavigation();
  const invitationToken =
    window.sessionStorage.getItem('invitation_token') ?? '';

  const invitationTokenType = window.sessionStorage.getItem('link_type');

  // When the email CTA URL (/register/register-with-email?type=project&...)
  // is opened, React Navigation's web linking config may route here via the
  // `register: 'register'` path match before getInitialRouteName() runs.
  // Redirect immediately to the correct preview screen so the user lands on
  // the project/task overview with the sign-up modal, not the registration flow.
  useEffect(() => {
    if (invitationTokenType === 'project' && invitationToken) {
      (navigation as any).replace('project-invitation-preview');
    } else if (invitationTokenType === 'task' && invitationToken) {
      (navigation as any).replace('task-invitation-preview');
    }
  }, []);

  const [updateMe] = useUpdateMeMutation();
  const initialValues: RegisterFormValues = {
    email: userInfo?.email || '',
    password: '',
    first_name: userInfo?.given_name || '',
    last_name: userInfo?.family_name || '',
    phone: '',
    invitation_token: invitationTokenType !== 'chat' ? invitationToken : '',
    provider: userInfo?.provider,
    uid: userInfo?.id,
    time_zone: guessTimeZone(),
    auth_token: '',
    email_verification_code: '',
    email_server_error: '',
    password_server_error: '',
    phone_server_error: '',
    verify_code_server_error: '',
  };

  const { registerForPushNotificationsAsync } = useNotification();
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await registerForPushNotificationsAsync();
        setDeviceToken(token || null);
      } catch (error) {
        console.error('Failed to get device token', error);
        setDeviceToken(null);
      }
    };

    fetchToken();
  }, []);

  const register = async (
    email: string,
    password: string,
    first_name: string,
    last_name: string,
    phone_number: string,
    invitation_token: string,
    setErrors: any,
    provider: string,
    uid: string,
    time_zone: string,
    auth_token: string,
    email_verification_code: string,
    team_name: string,
    team_emails: string[],
    step: number | undefined,
    setFieldValue: any,
    setFieldError: (field: string, message: string | undefined) => void,
    setStatus: (status: SocialSignupFormStatus) => void
  ) => {
    if (auth_token !== '') {
      await SecurityStorage.setItem('authToken', auth_token);
      localStorage.removeItem('copy_link_toast_shown');
      setAuthToken(auth_token);
    } else {
      if (provider === 'Apple') {
        const apple_auth = await SecurityStorage.getItem('authToken_apple');
        if (apple_auth) {
          await SecurityStorage.setItem('authToken', apple_auth);
          SecurityStorage.deleteItem('authToken_apple');
          await updateMe({
            variables: {
              attributes: {
                phoneNumber: phone_number,
                skillIds: [],
                timeZone: time_zone,
                name: [first_name, last_name]
                  .filter((item) => item !== '')
                  .join(' '),
              },
            },
            onCompleted: () => {
              localStorage.removeItem('copy_link_toast_shown');
              setAuthToken(apple_auth);
              window.sessionStorage.setItem('goto_welcome', '0');
              logEvent({
                name: 'sign_up',
                params: {
                  method: 'apple',
                  user_type: 'new',
                  source_page: window.location.pathname,
                  plan_selected: 'free',
                  invited: Boolean(invitationToken),
                },
              });
              trackEvent(MIXPANEL_EVENTS.USER_SIGNED_UP, {
                method: 'apple',
                invited: Boolean(invitationToken),
                ...getUtmEventProperties(),
              });
              trackEvent(MIXPANEL_EVENTS.ONBOARDING_COMPLETED, {
                platform: 'web',
                tutorial_type: 'signup',
              });
              setRegisterLoading(false);
            },
            // TA-10140 (QA round 3): this is the only real GraphQL mutation
            // inside registration, and it used to `alert(e)` — the raw
            // ApolloError object — with no field mapping at all. Map
            // extensions.field_errors onto the form via getFieldErrors(),
            // and fall back to the error's own readable message.
            // suppressGlobalMutationErrorToast avoids doubling up with the
            // TA-10139 global PERMISSION_DENIED/CONFLICT toast now that this
            // call site shows its own message.
            context: { suppressGlobalMutationErrorToast: true },
            onError: (e) => {
              // Code review (Copilot): this flow validates against
              // registerSocialSchema.web, which has no `*_server_error` tests,
              // and CreateProfileForGoogleAndApple.web.tsx renders
              // `errors.phone` / `errors.first_name`. Writing server_error
              // *values* here would set inert form state the user never sees,
              // so this path writes Formik errors directly — exactly what that
              // screen's own verifyPhoneNumber() handler does.
              const mutationFieldErrors = getRegisterMutationFieldErrors(e);
              const visibleFields = [
                ['phone', mutationFieldErrors.phone],
                ['first_name', mutationFieldErrors.firstName],
              ] as const;
              visibleFields.forEach(([field, message]) => {
                if (message) setFieldError(field, message);
              });

              // Nothing landed on a field this screen renders (an `email`
              // error, an unmapped key, or no field_errors at all) — say it
              // out loud instead of attaching it to an invisible input.
              const attachedToVisibleField = visibleFields.some(
                ([, message]) => !!message
              );
              if (!attachedToVisibleField) {
                alert(
                  mutationFieldErrors.email ??
                    getApolloErrorMessage(e) ??
                    i18n.t('shared:exceptionMsg')
                );
              }
              setRegisterLoading(false);
            },
          });
        }

        setRegisterLoading(false);
        return;
      }

      const xstep = step ?? 3;
      const register = `/api/v4/users.json`;
      const registerPayload = {
        user: {
          // TA-11025: social sign-up must match the identity token, so email
          // and uid come from the same live sign-in (a re-prompt may switch
          // Google accounts).
          email: ((provider && userInfo?.email) || email).toLowerCase(),
          password: password,
          terms_of_service: true,
          name: [first_name, last_name].filter((item) => item !== '').join(' '),
          phone_number: phone_number,
          invitation_token: invitation_token,
          provider: provider,
          uid: (provider && userInfo?.id) || uid,
          // TA-11025 (pairs with TA-11024): a social registration must carry
          // the live Google ID token; the backend no longer takes `provider`
          // alone as a reason to skip the email OTP.
          ...(provider && { identity_token: userInfo?.identity_token }),
          time_zone: time_zone,
          firebase_token: deviceToken,
          email_verification_code,
          platform: 'web',
        },
      };
      const authEndpoint = `${apiConfig.apiUrl}${register}`;
      await fetch(authEndpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerPayload),
      })
        .then(async (response) => {
          const body = await response.json().catch(() => null);
          return parseRegisterResponse(response.ok, body, {
            status: response.status,
            rateLimitMessage: i18n.t(RATE_LIMIT_ERROR_KEY),
          });
        })
        .then(async (result) => {
          if (isSocialIdentityError(result)) {
            // TA-11025: the Google token expired or didn't verify. Flag it so
            // the profile screen's button re-prompts Google (inside its click,
            // so the popup isn't blocked) before resubmitting.
            setRegisterLoading(false);
            setStatus({ socialReauthRequired: true });
            alert(
              i18n.t('models:onBoarding.error.socialSignInExpired', {
                provider: provider || 'Google',
              })
            );
          } else if (result.status === 'error') {
            setRegisterLoading(false);

            // TA-10140: every backend field error is mapped onto its inline
            // form field (see registerFormFieldErrors.ts) instead of only the
            // first key with a sentence-matching switch — so e.g. a response
            // carrying both email and phone errors shows both.
            const fieldErrors = getRegisterFieldErrors(result);
            // Code review (Copilot), two issues fixed here:
            // 1. server_error values were never cleared between submits, so a
            //    phone error from attempt #1 stayed visible when attempt #2
            //    only reported an email error.
            // 2. social signup (Google/Apple) validates against
            //    registerSocialSchema.web, which has NO `*_server_error`
            //    tests, so writing those values left the failure invisible on
            //    the profile screen. That path writes the Formik error
            //    directly instead — and only that, because setFieldValue
            //    triggers a re-validation whose (server-error-blind) result
            //    would immediately overwrite the error we just set.
            if (!isSocialSignup) {
              SERVER_ERROR_FIELDS.forEach(([, serverErrorKey]) => {
                setFieldValue(serverErrorKey, '');
              });
            }
            SERVER_ERROR_FIELDS.forEach(
              ([formikField, serverErrorKey, mappedKey]) => {
                const message = fieldErrors[mappedKey];
                if (!message) return;
                if (isSocialSignup) {
                  setFieldError(formikField, message);
                  return;
                }
                setFieldValue(serverErrorKey, message);
              }
            );

            const historyOffset = getRegisterHistoryOffset(fieldErrors, xstep);
            if (historyOffset !== null) {
              window.history.go(historyOffset);
            }
          } else {
            try {
              // TA-10886: registration never opens the 30-day trial welcome
              // modal anymore — the owner goes through Stripe checkout
              // instead, so this is always the normal "go to projects page
              // for tutorialization" state.
              window.sessionStorage.setItem('goto_welcome', '0');
            } catch (e) {
              // error reading value
              console.error('Error reading history', e);
            } finally {
              setFieldValue('auth_token', result.authToken);
              Sentry.setUser({
                email: email,
                username: uid,
              });
              window.sessionStorage.setItem('setKeepSignedIn', '1');
              logEvent({
                name: 'sign_up',
                params: {
                  method: provider?.toLowerCase() || 'email',
                  user_type: 'new',
                  source_page: window.location.pathname,
                  plan_selected: 'free',
                  invited: Boolean(invitation_token),
                },
              });
              trackEvent(MIXPANEL_EVENTS.USER_SIGNED_UP, {
                method: provider?.toLowerCase() || 'email',
                invited: Boolean(invitation_token),
                ...getUtmEventProperties(),
              });
              trackEvent(MIXPANEL_EVENTS.ONBOARDING_COMPLETED, {
                platform: 'web',
                tutorial_type: 'signup',
              });
              localStorage.removeItem('copy_link_toast_shown');

              // Deferred team creation (TA-10009 / TA-10886): the team is no
              // longer created here — the values are handed to the app stack,
              // which opens Stripe checkout for them. This must happen before
              // setAuthToken: that swaps AuthStack for AppStack synchronously,
              // and a MainPanel that resolves `me` first would read storage
              // once, find nothing, and never open checkout.
              // Trimmed: CreateTeam's schema accepts whitespace-only input,
              // and savePendingTeamSignup rejects it — without the same
              // predicate here, a blank name would skip the clear branch and
              // leave a previous registration's team in this tab.
              if (team_name?.trim()) {
                savePendingTeamSignup({
                  name: team_name,
                  emails: team_emails ?? [],
                  ownerEmail: email,
                });
              } else {
                // Skipping the team step is terminal: drop anything an
                // abandoned registration left in this tab so it is never
                // applied to the account that just signed up.
                clearPendingTeamSignup();
              }

              setAuthToken(result.authToken);
              await SecurityStorage.setItem('authToken', result.authToken);
              setRegisterLoading(false);
            }
          }
        })
        .catch((_err) => {
          setRegisterLoading(false);
          setErrors({
            email: 'network error',
          });
        });
    }
  };

  const buildForm = () => (
    <Formik
      initialValues={initialValues}
      validateOnMount
      onSubmit={(
        values,
        { setErrors, setFieldValue, setFieldError, setStatus }
      ) =>
        // TA-10896: this must return the register() promise, not just call
        // it -- Formik's submitForm() resolves as soon as onSubmit returns,
        // so an un-returned promise let callers (e.g. CreateTeam.web.tsx)
        // believe registration was done and clear their loading state while
        // the actual network request was still in flight.
        register(
          values.email,
          values.password,
          values.first_name,
          values.last_name,
          values.phone,
          values.invitation_token ?? '',
          setErrors,
          values.provider,
          values.uid,
          values.time_zone,
          values.auth_token,
          values.email_verification_code ?? '',
          values.team_name,
          values.team_emails,
          values.step,
          setFieldValue,
          setFieldError,
          setStatus
        )
      }
      validationSchema={isSocialSignup ? registerSocialSchema : registerSchema}>
      {() => <RegisterStackNew />}
    </Formik>
  );

  return (
    <>
      <Helmet>
        <meta
          name='description'
          content='Create your free TaskTag account using your email and start organizing tasks, managing projects, and boosting team productivity today.'
        />
      </Helmet>
      {buildForm()}
    </>
  );
};

export default RegisterForm;
